/**
 * The two responses a queued Slow (Gemma) chat turn adds to the chat API (MAR-1390).
 *
 * `POST /api/chat` answers a turn that resolves to Gemma with `QueuedChatTurnResponse` instead of an
 * NDJSON stream; the browser then follows `public_rag_jobs/{jobId}` over Firestore. Stopping that
 * turn is `POST /api/chat/queued/:jobId/cancel`, answered with `QueuedChatCancelResponse`.
 */

/** `POST /api/chat` for a turn that runs on Gemma. */
export interface QueuedChatTurnResponse {
  queued: true
  jobId: string
}

/** `POST /api/chat/queued/:jobId/cancel`. `cancelled: false` means the job had already finished. */
export interface QueuedChatCancelResponse {
  cancelled: boolean
}

export function isQueuedChatTurnResponse(value: unknown): value is QueuedChatTurnResponse {
  const v = value as Partial<QueuedChatTurnResponse> | null
  return !!v && v.queued === true && typeof v.jobId === 'string' && v.jobId.length > 0
}

/** The cancel endpoint for one job. One spelling, shared by the route and the browser. */
export function queuedChatCancelPath(jobId: string): string {
  return `/api/chat/queued/${encodeURIComponent(jobId)}/cancel`
}

// ── Progress and "Envíamelo por correo" (MAR-1424) ──────────────────────────────────────────────

/**
 * Where a queued Slow turn is, as the job doc's `stage` field says — in the order the user sees
 * them. `queued` is written at enqueue; the `web-chat-gemma` runner writes the rest:
 * `starting_model` only when the GPU was cold, `researching` once it answers (and again when a tool
 * runs), `writing` once the answer starts.
 */
export const QUEUED_CHAT_STAGES = ['queued', 'starting_model', 'researching', 'writing'] as const
export type QueuedChatStage = typeof QUEUED_CHAT_STAGES[number]

export function isQueuedChatStage(value: unknown): value is QueuedChatStage {
  return typeof value === 'string' && (QUEUED_CHAT_STAGES as readonly string[]).includes(value)
}

/** What the chat bubble shows of a queued turn, read off its job doc on every snapshot. */
export interface QueuedChatProgress {
  jobId: string
  stage: QueuedChatStage
  /** 1 = next in line. Only while the job is still `queued`: once claimed, the line means nothing. */
  position: number | null
  /** The owner pressed "Envíamelo por correo" (`requestQueuedChatJobEmail`). */
  emailRequested: boolean
  /** Where it will go, masked (`a•••@amin.app`). */
  email: string | null
}

/** `a•••@amin.app` — enough for the owner to recognise their address on a shared screen. */
export function maskEmail(address: unknown): string {
  const value = typeof address === 'string' ? address.trim() : ''
  const at = value.indexOf('@')
  if (at < 1 || at === value.length - 1) return ''
  return `${value[0]}•••${value.slice(at)}`
}

/** The doc fields → the bubble's view. Unknown or missing fields fall back to "queued, no place". */
export function readQueuedChatProgress(jobId: string, data: Record<string, unknown>): QueuedChatProgress {
  const position = data.status === 'queued' && typeof data.queuePosition === 'number' && data.queuePosition > 0
    ? Math.floor(data.queuePosition)
    : null
  const emailRequested = data.emailRequested === true
  return {
    jobId,
    stage: isQueuedChatStage(data.stage) ? data.stage : 'queued',
    position,
    emailRequested,
    email: emailRequested ? (maskEmail(data.notifyEmail) || null) : null,
  }
}

/** `POST /api/chat/queued/:jobId/email`. `requested: false` means the job had already finished. */
export interface QueuedChatEmailResponse {
  requested: boolean
  email: string | null
}

/** The "Envíamelo por correo" endpoint for one job. */
export function queuedChatEmailPath(jobId: string): string {
  return `/api/chat/queued/${encodeURIComponent(jobId)}/email`
}
